// @flow
import { type ActionType, createAction, handleActions } from 'redux-actions';
import produce from 'immer';
import { applyPenders } from 'redux-pender';
import * as BannersAPI from 'lib/api/banners';

const GET_RECENT_BANNERS = 'bannering/GET_RECENT_BANNERS';
const PREFETCH_RECENT_BANNERS = 'bannering/PREFETCH';
const REVEAL_PREFETCHED = 'bannering/REVEAL_PREFETCHED';
const CLEAR_USER_BANNERS = 'bannering/CLEAR_USER_BANNERS';
const GET_USER_BANNERS = 'bannering/GET_USER_BANNERS';
const PREFETCH_USER_BANNERS = 'bannering/PREFETCH_USER_BANNERS';
const GET_TRENDING_BANNERS = 'bannering/GET_TRENDING_BANNERS';
const PREFETCH_TRENDING_BANNERS = 'bannering/PREFETCH_TRENDING_BANNERS';
const GET_TAG_BANNERS = 'bannering/GET_TAG_BANNERS';
const PREFETCH_TAG_BANNERS = 'bannering/PREFETCH_TAG_BANNERS';
const GET_CATEGORY_BANNERS = 'bannering/GET_CATEGORY_BANNERS';
const PREFETCH_CATEGORY_BANNERS = 'bannering/PREFETCH_CATEGORY_BANNERS';
const CLEAR_CATEGORY_BANNERS = 'bannering/CLEAR_CATEGORY_BANNERS';
const CLEAR_TAG_BANNERS = 'bannering/CLEAR_TAG_BANNERS';
const GET_TEMP_BANNERS = 'bannering/GET_TEMP_BANNERS';
const PREFETCH_TEMP_BANNERS = 'bannering/PREFETCH_TEMP_BANNERS';
const REMOVE_TEMP_BANNER = 'bannering/REMOVE_TEMP_BANNER';
const FLUSH_LIST = 'bannering/FLUSH_LIST';

// potential improvement :: remove duplicates
export const actionCreators = {
  getRecentBanners: createAction(GET_RECENT_BANNERS, BannersAPI.getPublicBanners),
  prefetchRecentBanners: createAction(PREFETCH_RECENT_BANNERS, BannersAPI.getPublicBanners),
  revealPrefetched: createAction(REVEAL_PREFETCHED, (type: string) => type),
  clearUserBanners: createAction(CLEAR_USER_BANNERS),
  getUserBanners: createAction(
    GET_USER_BANNERS,
    BannersAPI.getUserBanners,
    (meta: { username: string, tag?: string }) => meta,
  ),
  prefetchUserBanners: createAction(PREFETCH_USER_BANNERS, BannersAPI.getUserBanners),
  getTrendingBanners: createAction(GET_TRENDING_BANNERS, BannersAPI.getTrendingBanners),
  prefetchTrendingBanners: createAction(PREFETCH_TRENDING_BANNERS, BannersAPI.getTrendingBanners),
  getTagBanners: createAction(
    GET_TAG_BANNERS,
    BannersAPI.getPublicBannersByTag,
    (meta: { tag: string }) => meta,
  ),
  prefetchTagBanners: createAction(PREFETCH_TAG_BANNERS, BannersAPI.getPublicBannersByTag),
  getCategoryBanners: createAction(
    GET_CATEGORY_BANNERS,
    BannersAPI.getPublicBannersByCategory,
    (meta: { category: string, sort?: string }) => meta,
  ),
  prefetchCategoryBanners: createAction(
    PREFETCH_CATEGORY_BANNERS,
    BannersAPI.getPublicBannersByCategory,
  ),
  clearCategoryBanners: createAction(CLEAR_CATEGORY_BANNERS),
  clearTagBanners: createAction(CLEAR_TAG_BANNERS),
  getTempBanners: createAction(GET_TEMP_BANNERS, BannersAPI.getTempBanners),
  prefetchTempBanners: createAction(PREFETCH_TEMP_BANNERS, BannersAPI.getTempBanners),
  removeTempBanner: createAction(REMOVE_TEMP_BANNER, (bannerId: string) => bannerId),
  flushList: createAction(FLUSH_LIST),
};

export type BannerItem = {
  id: string,
  title: string,
  body: string,
  thumbnail: string,
  is_markdown: boolean,
  is_private: boolean,
  released_at: string,
  created_at: string,
  updated_at: string,
  tags: string[],
  categories: any[],
  url_slug: string,
  likes: number,
  comments_count: number,
  user: {
    id: string,
    username: string,
    bannering_name: string,
    short_bio: string,
    thumbnail: string,
  },
  meta: any,
};

export type BanneringSet = {
  banners: ?(BannerItem[]),
  prefetched: ?(BannerItem[]),
  end: boolean,
};

export type Bannering = {
  trendingMap: {
    [id: string]: boolean,
  },
  trending: BanneringSet,
  recent: BanneringSet,
  user: BanneringSet & { currentUsername: ?string, currentTag: ?string },
  tag: BanneringSet & { currentTag: ?string },
  category: BanneringSet & { currentCategory: ?string },
  temp: BanneringSet,
};

type RevealPrefetchedAction = ActionType<typeof actionCreators.revealPrefetched>;
type BannersResponseAction = {
  type: string,
  payload: {
    data: BannerItem[],
    category?: ?string,
    sort?: ?string,
  },
  meta: any,
};
type RemoveTempBannerAction = ActionType<typeof actionCreators.removeTempBanner>;

export interface BanneringActionCreators {
  getRecentBanners(cursor: string, category: string, sort: string): any;

  prefetchRecentBanners(cursor: string, category: string, sort: string): any;

  revealPrefetched(type: string): any;

  getUserBanners(payload: BannersAPI.GetUserBannersPayload): any;

  prefetchUserBanners(payload: BannersAPI.GetUserBannersPayload): any;

  getTrendingBanners(): any;

  prefetchTrendingBanners(offset: number): any;

  getTagBanners(payload: BannersAPI.GetPublicBannersByTagPayload): any;

  prefetchTagBanners(payload: BannersAPI.GetPublicBannersByTagPayload): any;

  getCategoryBanners(payload: BannersAPI.GetPublicBannersByCategoryPayload): any;

  prefetchCategoryBanners(payload: BannersAPI.GetPublicBannersByCategoryPayload): any;

  getTempBanners(payload: BannersAPI.GetTempBannersPayload): any;

  prefetchTempBanners(payload: BannersAPI.GetTempBannersPayload): any;

  clearCategoryBanners(): any;

  clearTagBanners(): any;

  clearUserBanners(): any;

  removeTempBanner(bannerId: string): any;

  flushList(): any;
}

const initialBanneringSet = {
  banners: null,
  prefetched: null,
  end: false,
};

const initialState: Bannering = {
  trendingMap: {},
  trending: initialBanneringSet,
  recent: initialBanneringSet,
  user: { ...initialBanneringSet, currentUsername: null, currentTag: null },
  tag: { ...initialBanneringSet, currentTag: null },
  category: { ...initialBanneringSet, currentCategory: null },
  temp: initialBanneringSet,
};

const reducer = handleActions(
  {
    [REVEAL_PREFETCHED]: (state, action: RevealPrefetchedAction) => {
      return produce(state, draft => {
        const { payload } = action;
        const { banners, prefetched } = draft[payload];
        if (banners && prefetched) {
          banners.push(...prefetched);
          draft[payload].prefetched = null;
        }
      });
    },
    [CLEAR_USER_BANNERS]: state => {
      return produce(state, draft => {
        draft.user = {
          banners: null,
          prefetched: null,
          end: false,
          currentUsername: null,
          currentTag: null,
        };
      });
    },
    [CLEAR_TAG_BANNERS]: state => {
      return produce(state, draft => {
        draft.tag = {
          banners: null,
          prefetched: null,
          end: false,
          currentTag: null,
        };
      });
    },
    [CLEAR_CATEGORY_BANNERS]: state => {
      return produce(state, draft => {
        draft.category = {
          banners: null,
          prefetched: null,
          end: false,
          currentCategory: null,
        };
      });
    },
    [REMOVE_TEMP_BANNER]: (state, { payload }: RemoveTempBannerAction) => {
      return produce(state, draft => {
        if (!draft.temp.banners) return;
        draft.temp.banners = draft.temp.banners.filter(p => p.id !== payload);
      });
    },
    [FLUSH_LIST]: state => {
      return initialState;
    },
  },
  initialState,
);

export default applyPenders(reducer, [
  {
    type: GET_RECENT_BANNERS,
    onSuccess: (state: Bannering, action: BannersResponseAction) => {
      const { category, sort } = action.payload;
      return produce(state, draft => {
        draft.recent = {
          category,
          sort,
          end: false,
          banners: action.payload.data,
          prefetched: null,
        };
      });
    },
  },
  {
    type: PREFETCH_RECENT_BANNERS,
    onSuccess: (state: Bannering, action: BannersResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.recent.prefetched = data;
        if (data && data.length === 0) {
          draft.recent.end = true;
        }
      });
    },
  },
  {
    type: GET_TAG_BANNERS,
    onSuccess: (state: Bannering, action: BannersResponseAction) => {
      return produce(state, draft => {
        draft.tag = {
          end: action.payload.data.length < 20,
          banners: action.payload.data,
          prefetched: null,
          currentTag: action.meta.tag,
        };
      });
    },
  },
  {
    type: PREFETCH_TAG_BANNERS,
    onSuccess: (state: Bannering, action: BannersResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.tag.prefetched = data;
        if (data && data.length === 0) {
          draft.tag.end = true;
        }
      });
    },
  },
  {
    type: GET_CATEGORY_BANNERS,
    onSuccess: (state: Bannering, action: BannersResponseAction) => {
      return produce(state, draft => {
        draft.category = {
          end: action.payload.data.length < 20,
          banners: action.payload.data,
          prefetched: null,
          currentCategory: action.meta.category,
          sort: action.meta.sort,
        };
      });
    },
  },
  {
    type: PREFETCH_CATEGORY_BANNERS,
    onSuccess: (state: Bannering, action: BannersResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.category.prefetched = data;
        if (data && data.length === 0) {
          draft.category.end = true;
        }
      });
    },
  },
  {
    type: GET_TRENDING_BANNERS,
    onSuccess: (state: Bannering, action: BannersResponseAction) => {
      return produce(state, draft => {
        action.payload.data.forEach(banner => {
          draft.trendingMap[banner.id] = true;
        });
        draft.trending = {
          end: false,
          banners: action.payload.data,
          prefetched: null,
        };
      });
    },
  },
  {
    type: PREFETCH_TRENDING_BANNERS,
    onSuccess: (state: Bannering, action: BannersResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        const filtered = data.filter(banner => !state.trendingMap[banner.id]);
        draft.trending.prefetched = filtered;
        filtered.forEach(banner => {
          draft.trendingMap[banner.id] = true;
        });
        if (data && data.length === 0) {
          draft.trending.end = true;
        }
      });
    },
  },
  {
    type: GET_USER_BANNERS,
    onPending: (state: Bannering) => {
      return produce(state, draft => {
        draft.user.end = false;
        draft.user.prefetched = null;
      });
    },
    onSuccess: (state: Bannering, action: BannersResponseAction) => {
      return produce(state, draft => {
        draft.user.banners = action.payload.data;
        draft.user.currentUsername = action.meta.username;
        if (action.meta.tag) {
          draft.user.currentTag = action.meta.tag;
        }
        if (action.payload.data.length < 20) {
          draft.user.end = true;
        }
      });
    },
  },
  {
    type: PREFETCH_USER_BANNERS,
    onSuccess: (state: Bannering, action: BannersResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.user.prefetched = data;
        if (data && data.length === 0) {
          draft.user.end = true;
        }
      });
    },
  },
  {
    type: GET_TEMP_BANNERS,
    onPending: (state: Bannering) => {
      return produce(state, draft => {
        draft.temp.end = false;
        draft.temp.prefetched = null;
      });
    },
    onSuccess: (state: Bannering, action: BannersResponseAction) => {
      return produce(state, draft => {
        draft.temp.banners = action.payload.data;
        if (action.payload.data.length < 20) {
          draft.temp.end = true;
        }
      });
    },
  },
  {
    type: PREFETCH_TEMP_BANNERS,
    onSuccess: (state: Bannering, action: BannersResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.temp.prefetched = data;
        if (data && data.length === 0) {
          draft.temp.end = true;
        }
      });
    },
  },
]);



// WEBPACK FOOTER //
// ./src/store/modules/bannering.js